import { useState, useRef, useEffect } from "react";
import { usePublisher, insertMarkdown$, Separator, ButtonWithTooltip, defaultSvgIcons, Select } from "@mdxeditor/editor";
import { EditorView } from "@codemirror/view";
import { undo, redo } from "@codemirror/commands";
import { Modal, Popover } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { SbInput } from "uiCore";

// Curly-brace icon — matches MDXEditor toolbar icon sizing, color via currentColor
const MergeFieldIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"
         fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 4c-1 0-2 .9-2 2v3c0 1-.9 1.5-2 2 1.1.5 2 1 2 2v3c0 1.1 1 2 2 2" />
        <path d="M17 4c1 0 2 .9 2 2v3c0 1 .9 1.5 2 2-1.1.5-2 1-2 2v3c0 1.1-1 2-2 2" />
    </svg>
);

// ─── CodeMirror source-mode helpers ──────────────────────────────────────────

export function findSourceCmView(editorRef) {
    if (!editorRef?.current) return null;
    const els = editorRef.current.querySelectorAll(".cm-editor");
    for (const el of els) {
        if (!el.closest(".mdxeditor-root-contenteditable")) {
            return EditorView.findFromDOM(el);
        }
    }
    return null;
}

export function toggleInlineFormat(view, before, after = before) {
    const { from, to } = view.state.selection.main;
    const sel = view.state.doc.sliceString(from, to);
    const isWrapped =
        sel.startsWith(before) && sel.endsWith(after) &&
        sel.length >= before.length + after.length;

    if (isWrapped) {
        const inner = sel.slice(before.length, sel.length - after.length);
        view.dispatch({
            changes: { from, to, insert: inner },
            selection: { anchor: from, head: from + inner.length },
        });
    } else {
        view.dispatch({
            changes: { from, to, insert: before + sel + after },
            selection: { anchor: from + before.length, head: from + before.length + sel.length },
        });
    }
    view.focus();
}

export function toggleLinePrefix(view, prefix) {
    const { from, to } = view.state.selection.main;
    const lineFrom = view.state.doc.lineAt(from);
    const lineTo   = view.state.doc.lineAt(to);
    const isHeadingPrefix = /^#{1,6} /.test(prefix);
    const changes = [];

    for (let i = lineFrom.number; i <= lineTo.number; i++) {
        const line = view.state.doc.line(i);

        if (prefix === "") {
            const m = line.text.match(/^(#{1,6} |- |\d+\. )/);
            if (m) changes.push({ from: line.from, to: line.from + m[0].length, insert: "" });
        } else if (line.text.startsWith(prefix)) {
            changes.push({ from: line.from, to: line.from + prefix.length, insert: "" });
        } else if (isHeadingPrefix) {
            const existingHeading = line.text.match(/^#{1,6} /);
            if (existingHeading) {
                changes.push({ from: line.from, to: line.from + existingHeading[0].length, insert: prefix });
            } else {
                changes.push({ from: line.from, insert: prefix });
            }
        } else {
            changes.push({ from: line.from, insert: prefix });
        }
    }

    if (changes.length) view.dispatch({ changes, scrollIntoView: true });
    view.focus();
}

// ─── Block type config ────────────────────────────────────────────────────────

const BLOCK_TYPES = [
    { label: "Paragraph", value: "paragraph", prefix: "" },
    { label: "Heading 1", value: "h1",        prefix: "# " },
    { label: "Heading 2", value: "h2",        prefix: "## " },
    { label: "Heading 3", value: "h3",        prefix: "### " },
    { label: "Heading 4", value: "h4",        prefix: "#### " },
    { label: "Heading 5", value: "h5",        prefix: "##### " },
    { label: "Heading 6", value: "h6",        prefix: "###### " },
];

// ─── Link Dialog ──────────────────────────────────────────────────────────────

const LinkDialog = ({ open, initialText, onSave, onCancel }) => {
    const [url, setUrl]             = useState("");
    const [anchorText, setAnchorText] = useState("");
    const [title, setTitle]         = useState("");

    useEffect(() => {
        if (open) {
            setUrl("");
            setAnchorText(initialText ?? "");
            setTitle("");
        }
    }, [open, initialText]);

    const handleSave = () => {
        if (!url.trim()) return;
        const text      = anchorText.trim() || url.trim();
        const titlePart = title.trim() ? ` "${title.trim()}"` : "";
        onSave(`[${text}](${url.trim()}${titlePart})`);
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            onOk={handleSave}
            okText="Save"
            okButtonProps={{ disabled: !url.trim() }}
            title={null}
            centered
            width={420}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "8px 0 4px" }}>
                <div>
                    <div style={{ marginBottom: 6, fontWeight: 500 }}>URL</div>
                    <SbInput
                        placeholder="https://example.com"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onPressEnter={handleSave}
                        autoFocus
                    />
                </div>
                <div>
                    <div style={{ marginBottom: 6, fontWeight: 500 }}>Anchor text</div>
                    <SbInput
                        value={anchorText}
                        onChange={(e) => setAnchorText(e.target.value)}
                        onPressEnter={handleSave}
                    />
                </div>
                <div>
                    <div style={{ marginBottom: 6, fontWeight: 500 }}>Link title</div>
                    <SbInput
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onPressEnter={handleSave}
                    />
                </div>
            </div>
        </Modal>
    );
};

// ─── Image Dialog ─────────────────────────────────────────────────────────────

const ImageDialog = ({ open, initialAlt, onSave, onCancel }) => {
    const [url, setUrl]     = useState("");
    const [alt, setAlt]     = useState("");
    const [title, setTitle] = useState("");

    useEffect(() => {
        if (open) {
            setUrl("");
            setAlt(initialAlt ?? "");
            setTitle("");
        }
    }, [open, initialAlt]);

    const handleSave = () => {
        if (!url.trim()) return;
        const titlePart = title.trim() ? ` "${title.trim()}"` : "";
        onSave(`![${alt.trim()}](${url.trim()}${titlePart})`);
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            onOk={handleSave}
            okText="Save"
            okButtonProps={{ disabled: !url.trim() }}
            title="Upload an image"
            centered
            width={420}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "8px 0 4px" }}>
                <div>
                    <div style={{ marginBottom: 6, fontWeight: 500 }}>Add an image from an URL:</div>
                    <SbInput
                        placeholder="https://example.com/image.png"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onPressEnter={handleSave}
                        autoFocus
                    />
                </div>
                <div>
                    <div style={{ marginBottom: 6, fontWeight: 500 }}>Alt:</div>
                    <SbInput
                        value={alt}
                        onChange={(e) => setAlt(e.target.value)}
                        onPressEnter={handleSave}
                    />
                </div>
                <div>
                    <div style={{ marginBottom: 6, fontWeight: 500 }}>Title:</div>
                    <SbInput
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onPressEnter={handleSave}
                    />
                </div>
            </div>
        </Modal>
    );
};

// ─── Merge Field Popover ──────────────────────────────────────────────────────

// fields: Array<{ label, value }> (flat)
//      OR Array<{ group: string, fields: Array<{ label, value }> }> (grouped)
const MergeFieldPopover = ({ fields, onInsert }) => {
    const [search, setSearch]   = useState("");
    const [open, setOpen]       = useState(false);

    const isGrouped = fields.length > 0 && "group" in fields[0];
    const groups    = isGrouped ? fields : [{ group: null, fields }];

    const query   = search.toLowerCase();
    const visible = groups
        .map(({ group, fields: gf }) => ({
            group,
            fields: gf.filter((f) => !query || f.label.toLowerCase().includes(query)),
        }))
        .filter((g) => g.fields.length > 0);

    const content = (
        <div style={{ width: 260 }}>
            <SbInput
                prefix={<SearchOutlined />}
                placeholder="Search merge field"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: 8 }}
            />
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
                {visible.length === 0 && (
                    <div style={{ padding: "12px", color: "#999", textAlign: "center" }}>
                        No fields found
                    </div>
                )}
                {visible.map(({ group, fields: gf }) => (
                    <div key={group ?? "__flat__"}>
                        {group && (
                            <div style={{
                                padding: "8px 12px 4px",
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#888",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                            }}>
                                {group}
                            </div>
                        )}
                        {gf.map((f) => (
                            <div
                                key={f.value}
                                style={{ padding: "7px 16px", cursor: "pointer", borderRadius: 4, fontSize: 14 }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f5f5"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onInsert(f.value);
                                    setOpen(false);
                                    setSearch("");
                                }}
                            >
                                {f.label}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}
            placement="bottomLeft"
        >
            {/* span gives Ant Design a real DOM ref for anchor positioning —
                ButtonWithTooltip is not a forwardRef so Popover can't find it */}
            <span style={{ display: "inline-flex" }}>
                <ButtonWithTooltip title="Insert Merge Field">
                    <MergeFieldIcon />
                </ButtonWithTooltip>
            </span>
        </Popover>
    );
};

// Rich-text toolbar version: same popup UI, but inserts via Lexical (insertMarkdown$)
// CoreMarkdownEditor passes { fields, editorRef } — editorRef is unused here since
// we're always in rich-text mode and Lexical is the active editor.
export const MergeFieldButton = ({ fields }) => {
    const insertMd = usePublisher(insertMarkdown$);
    return <MergeFieldPopover fields={fields} onInsert={insertMd} />;
};

// ─── Source Mode Toolbar ──────────────────────────────────────────────────────

const SourceModeToolbar = ({ editorRef, mergeFields, showInsertImage = true }) => {
    const [blockType,  setBlockType]  = useState("paragraph");
    const [linkOpen,   setLinkOpen]   = useState(false);
    const [imageOpen,  setImageOpen]  = useState(false);
    const [linkText,   setLinkText]   = useState("");
    const [imageAlt,   setImageAlt]   = useState("");
    const savedRange = useRef(null);
    const insertMd   = usePublisher(insertMarkdown$);

    const getView  = () => findSourceCmView(editorRef);

    // Detect block type of the cursor's current line on mount
    useEffect(() => {
        const view = findSourceCmView(editorRef);
        if (!view) return;
        const { from } = view.state.selection.main;
        const line = view.state.doc.lineAt(from);
        const m = line.text.match(/^(#{1,6}) /);
        if (m) setBlockType(`h${m[1].length}`);
        else    setBlockType("paragraph");
    }, [editorRef]);
    const dispatch = (fn) => { const v = getView(); if (v) fn(v); };

    const captureAndOpen = (setter, textSetter) => {
        const view = getView();
        if (view) {
            const { from, to } = view.state.selection.main;
            savedRange.current = { from, to };
            textSetter(view.state.doc.sliceString(from, to));
        }
        setter(true);
    };

    const insertAtSaved = (markdown) => {
        const view = getView();
        if (view && savedRange.current) {
            const { from, to } = savedRange.current;
            view.dispatch({ changes: { from, to, insert: markdown }, scrollIntoView: true });
            view.focus();
        } else {
            insertMd(markdown);
        }
        savedRange.current = null;
    };

    const btn = (title, icon, onMD) => (
        <ButtonWithTooltip
            key={title}
            title={title}
            onMouseDown={(e) => { e.preventDefault(); dispatch(onMD); }}
        >
            {icon}
        </ButtonWithTooltip>
    );

    const handleBlockTypeChange = (value) => {
        const item = BLOCK_TYPES.find((b) => b.value === value);
        if (!item) return;
        dispatch((v) => toggleLinePrefix(v, item.prefix));
        setBlockType(value);
    };

    const handleInsertMergeField = (value) => {
        const view = getView();
        if (view) {
            const { from, to } = view.state.selection.main;
            view.dispatch({ changes: { from, to, insert: value } });
            view.focus();
        } else {
            insertMd(value);
        }
    };

    return (
        <>
            <LinkDialog
                open={linkOpen}
                initialText={linkText}
                onSave={(md) => { insertAtSaved(md); setLinkOpen(false); }}
                onCancel={() => { setLinkOpen(false); savedRange.current = null; }}
            />
            <ImageDialog
                open={imageOpen}
                initialAlt={imageAlt}
                onSave={(md) => { insertAtSaved(md); setImageOpen(false); }}
                onCancel={() => { setImageOpen(false); savedRange.current = null; }}
            />

            {btn("Undo", defaultSvgIcons.undo, (v) => undo(v))}
            {btn("Redo", defaultSvgIcons.redo, (v) => redo(v))}
            <Separator />

            <Select
                value={blockType}
                onChange={handleBlockTypeChange}
                triggerTitle="Block type"
                placeholder="Block type"
                items={BLOCK_TYPES.map(({ label, value }) => ({ label, value }))}
            />
            <Separator />

            {btn("Bold",      defaultSvgIcons.format_bold,       (v) => toggleInlineFormat(v, "**"))}
            {btn("Italic",    defaultSvgIcons.format_italic,     (v) => toggleInlineFormat(v, "_"))}
            {btn("Underline", defaultSvgIcons.format_underlined, (v) => toggleInlineFormat(v, "<u>", "</u>"))}
            <Separator />

            {btn("Bulleted list", defaultSvgIcons.format_list_bulleted, (v) => toggleLinePrefix(v, "- "))}
            {btn("Numbered list", defaultSvgIcons.format_list_numbered, (v) => toggleLinePrefix(v, "1. "))}
            <Separator />

            <ButtonWithTooltip
                title="Create link"
                onMouseDown={(e) => { e.preventDefault(); captureAndOpen(setLinkOpen, setLinkText); }}
            >
                {defaultSvgIcons.link}
            </ButtonWithTooltip>

            {showInsertImage && (
                <ButtonWithTooltip
                    title="Insert image"
                    onMouseDown={(e) => { e.preventDefault(); captureAndOpen(setImageOpen, setImageAlt); }}
                >
                    {defaultSvgIcons.add_photo}
                </ButtonWithTooltip>
            )}

            {btn("Insert thematic break", defaultSvgIcons.horizontal_rule, (v) => {
                const { from } = v.state.selection.main;
                const line = v.state.doc.lineAt(from);
                const insert = line.text.trim() === "" ? "---\n" : "\n\n---\n";
                v.dispatch({ changes: { from: line.to, insert }, scrollIntoView: true });
                v.focus();
            })}

            {btn("Insert table", defaultSvgIcons.table, (v) => {
                const { from, to } = v.state.selection.main;
                const table = "\n| Column 1 | Column 2 | Column 3 |\n| --- | --- | --- |\n| Cell | Cell | Cell |\n";
                v.dispatch({ changes: { from, to, insert: table }, scrollIntoView: true });
                v.focus();
            })}

            {mergeFields.length > 0 && (
                <MergeFieldPopover fields={mergeFields} onInsert={handleInsertMergeField} />
            )}
        </>
    );
};

export default SourceModeToolbar;
